import React, { useEffect, useState } from 'react';
import SettingsDialog from './SettingsDialog';
import { getApiUrl } from '../config/serverEndpoint';

export default function DictionaryAdminPanel({ username, onClose }) {
  const [entries, setEntries] = useState([]);
  const [version, setVersion] = useState(null);
  const [curatedRemaining, setCuratedRemaining] = useState([]);
  const [reasons, setReasons] = useState({});
  const [meanings,setMeanings]=useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [removeWord, setRemoveWord] = useState('');
  const [removeReason, setRemoveReason] = useState('');

  const request = async (path, payload = {}) => {
    const response = await fetch(getApiUrl(path), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, reconnectToken: sessionStorage.getItem('reconnectToken'), ...payload }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'İşlem başarısız.');
    return data;
  };

  const refresh = async () => {
    const data = await request('/admin/dictionary/list');
    setEntries(data.suggestions);
    setVersion(data.currentVersion);
    setCuratedRemaining(data.curatedRemaining || []);
  };

  useEffect(() => {
    const update = () => refresh().catch(err => setError(err.message));
    update();
    window.addEventListener('kelimo-player-name-updated', update);
    return () => window.removeEventListener('kelimo-player-name-updated', update);
  }, [username]);

  const decide = async (entry, action) => {
    const reason = (reasons[entry.word] || '').trim();
    if (reason.length < 5) { setError('Kaynak veya ret gerekçesi en az 5 karakter olmalı.'); return; }
    setBusy(true); setError(''); setMessage('');
    try {
      await request('/admin/dictionary/decision', { word: entry.word, action, reason, meaning:meanings[entry.word]||'' });
      await refresh();
      setMessage(`${entry.word}: ${action === 'approve' ? 'anlamıyla yayımlandı; yeni oyunlarda geçerli' : 'reddedildi'}.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await request('/admin/dictionary/publish');
      await refresh();
      setMessage(`${result.additions.length} kelime sözlük v${result.currentVersion} ile yeni maçlara açıldı. Devam eden maçlar eski sürümlerinde kaldı.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const pending = entries.filter(entry => entry.status === 'pending');
  const approved = entries.filter(entry => entry.status === 'approved');

  const publishCurated = async () => {
    if (!window.confirm(`${curatedRemaining.join(', ')} kelimeleri yalnızca yeni maçların sözlüğüne eklensin mi?`)) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await request('/admin/dictionary/publish-curated');
      await refresh();
      setMessage(`${result.additions.join(', ')} kelimeleri sözlük v${result.currentVersion} ile yayımlandı.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    const word = removeWord.trim().toLocaleLowerCase('tr-TR');
    if (removeReason.trim().length < 5) { setError('Çıkarma gerekçesi en az 5 karakter olmalı.'); return; }
    if (!window.confirm(`“${word}” yeni oyunların sözlüğünden çıkarılsın mı? Açık maçlar eski sürümü kullanır.`)) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await request('/admin/dictionary/remove', { word, reason: removeReason.trim() });
      await refresh();
      setRemoveWord(''); setRemoveReason('');
      setMessage(`${word} sözlük v${result.currentVersion} ile yeni oyunlardan çıkarıldı.`);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return <SettingsDialog className="dictionary-dialog" title="Sözlük yönetimi" onClose={onClose}>
      <div className="dictionary-summary"><span>Sözlük<strong>v{version ?? '…'}</strong></span><span>İncelenecek<strong>{pending.length}</strong></span><span>Yayımlanacak<strong>{approved.length}</strong></span></div>
      {error && <p role="alert" className="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {message && <p role="status" className="mt-3 rounded-lg bg-emerald-50 p-2 text-sm text-emerald-800">{message}</p>}
      {curatedRemaining.length > 0 && <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <h3 className="font-bold text-emerald-900">Doğrulanan kelimeler</h3>
        <p className="mt-1 text-sm text-slate-700">Türk Dil Kurumu sözlüğünde ayrı kaydı olan kelimeler: {curatedRemaining.join(', ')}. Çekimli kelime veya fiil içermez.</p>
        <button type="button" disabled={busy} onClick={publishCurated} className="mt-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">Bu {curatedRemaining.length} kelimeyi yayımla</button>
      </div>}
      <div className="mt-3 space-y-3">
        {entries.length === 0 && <p className="text-sm text-slate-600">Henüz kelime önerisi yok.</p>}
        {[...entries].sort((a,b) => Number(b.status === 'pending') - Number(a.status === 'pending')).map(entry => <article key={entry.word} className={`dictionary-entry status-${entry.status} rounded-xl border border-slate-200 bg-white p-3`}>
          <div className="flex flex-wrap items-center justify-between gap-2"><strong className="text-lg text-indigo-900">{entry.word}</strong><span className="rounded-full bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700">{{pending: 'İncelenecek', approved: 'Kabul edildi', published: 'Yayımlandı', rejected: 'Reddedildi'}[entry.status] || entry.status}</span></div>
          {(entry.reporters || []).map((reporter, index) => <p key={`${reporter.username}-${index}`} className="mt-1 break-words text-sm text-slate-700">{reporter.displayName || 'Oyuncu'}: {reporter.note || 'Açıklama yok'}</p>)}
          {entry.review && <p className="mt-1 text-xs text-slate-500">Karar kaydı: {entry.review.reason}</p>}
          {['pending','approved'].includes(entry.status) && <div className="mt-2">
            <label className="block text-xs font-bold text-slate-600" htmlFor={`reason-${entry.word}`}>Kaynak veya ret gerekçesi</label>
            <input id={`reason-${entry.word}`} maxLength={300} value={reasons[entry.word] || ''} onChange={event => setReasons(current => ({ ...current, [entry.word]: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Doğruladığın kaynağı veya ret gerekçesini yaz" />
            <label className="mt-2 block text-xs font-bold" htmlFor={`meaning-${entry.word}`}>Anlam açıklaması</label><textarea id={`meaning-${entry.word}`} maxLength={4000} value={meanings[entry.word]??entry.meaning?.[0]?.text??''} onChange={event=>setMeanings(current=>({...current,[entry.word]:event.target.value}))} className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-900" placeholder="Anlamı yaz; mevcut kayıt varsa boş bırakılabilir"/>
            <div className="mt-2 flex gap-2"><button type="button" disabled={busy} onClick={() => decide(entry, 'approve')} className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">Onayla ve yayımla</button><button type="button" disabled={busy} onClick={() => decide(entry, 'reject')} className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">Reddet</button></div>
          </div>}
        </article>)}
      </div>
      <div className="dictionary-publish mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
        <p className="text-xs text-indigo-900">Yayımlama yeni oyunlar için sözlük sürümü oluşturur; açık maçların puanını değiştirmez.</p>
        <button type="button" disabled={busy || approved.length === 0} onClick={publish} className="mt-2 w-full rounded-lg bg-indigo-700 px-4 py-2.5 font-bold text-white disabled:opacity-50">Kabul edilen {approved.length} kelimeyi yayımla</button>
      </div>
      <div className="mt-4 rounded-xl border border-rose-200 bg-white p-3">
        <h3 className="font-bold text-slate-900">Yanlış kelimeyi çıkar</h3>
        <p className="mt-1 text-xs text-slate-600">Yalnızca yeni oyunlarda geçerli olur; devam eden maçların sözlüğü korunur.</p>
        <label className="mt-2 block text-xs font-bold" htmlFor="remove-dictionary-word">Kelime</label>
        <input id="remove-dictionary-word" value={removeWord} onChange={event => setRemoveWord(event.target.value)} maxLength={10} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Çıkarılacak kelime" />
        <label className="mt-2 block text-xs font-bold" htmlFor="remove-dictionary-reason">Gerekçe</label>
        <input id="remove-dictionary-reason" value={removeReason} onChange={event => setRemoveReason(event.target.value)} maxLength={300} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Yanlış kelime veya kaynak gerekçesi" />
        <button type="button" disabled={busy || !removeWord.trim() || removeReason.trim().length < 5} onClick={remove} className="mt-2 rounded-lg bg-rose-700 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">Kelimeyi çıkar</button>
      </div>
  </SettingsDialog>;
}
